// legalDocument — load one legal document: the database text first, the bundled privacy copy
// when it cannot be loaded (PROD-763, Dr. Rami list-2 S11, R31 amended).
//
// Plain functions so scripts/check-privacy-fallback.mjs can execute them against a stubbed fetch.
// useLegalContent (components/LegalMarkdown.jsx) is the only React caller.
//   · database: GET /legal-content answered ok with text for the document -> source 'database',
//     version = the answer's updated_at (null from an API that does not send it yet);
//   · anything else (not ok, a throw, non-JSON, no text, or no answer within
//     LEGAL_LOAD_TIMEOUT_MS) -> privacy: BUNDLED_PRIVACY, source 'bundled'; terms: no text (the
//     caller shows <LegalLoadFailed>; the decision covers the privacy text only).
import { BUNDLED_PRIVACY } from './bundledPrivacy'

export const LEGAL_LOAD_TIMEOUT_MS = 10000

const NONE = Object.freeze({ content: '', source: null, version: null })

/** @returns {Promise<{content: string, source: 'database'|'bundled'|null, version: string|null}>} never rejects */
export async function loadLegalDocument(type, { url, fetchImpl, timeoutMs = LEGAL_LOAD_TIMEOUT_MS } = {}) {
  const key = type === 'terms' ? 'terms_conditions' : 'privacy_policy'
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null
  let timer = null
  const timedOut = new Promise((resolve) => {
    timer = setTimeout(() => { if (ctrl) ctrl.abort(); resolve(null) }, timeoutMs)
  })
  const fromDatabase = (async () => {
    const res = await fetchImpl(url, ctrl ? { signal: ctrl.signal } : undefined)
    if (!res || !res.ok) return null
    const data = await res.json()
    const text = data && typeof data[key] === 'string' ? data[key] : ''
    if (!text.trim()) return null
    return { content: text, source: 'database', version: typeof data.updated_at === 'string' ? data.updated_at : null }
  })()
  let doc = null
  try {
    doc = await Promise.race([fromDatabase, timedOut])
  } catch {
    doc = null
  } finally {
    clearTimeout(timer)
  }
  if (doc) return doc
  if (type === 'terms') return NONE
  return { content: BUNDLED_PRIVACY.text, source: 'bundled', version: BUNDLED_PRIVACY.version }
}

/** What the consent sends about the privacy text the app holds: {source, version}, or null while unknown. */
export function privacyConsentClaim(doc) {
  if (!doc || doc.loading || (doc.source !== 'database' && doc.source !== 'bundled')) return null
  return { source: doc.source, version: typeof doc.version === 'string' ? doc.version : null }
}
